// Angular
import {
	CommonModule,
	HashLocationStrategy,
	LocationStrategy,
} from "@angular/common";
import { LOCALE_ID, NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

// Shared
import { SharedModule } from "./shared.module";

// Core
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
	MsalBroadcastService,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptor,
	MsalInterceptorConfiguration,
	MsalModule,
	MsalService,
} from "@azure/msal-angular";
import { AppComponent } from "./app.component";

// Pages
import { CebePageComponent } from "./modules/main/pages/cebe/cebe.page";
import { ExamplePageComponent } from "./modules/main/pages/example/example.page";
import { HomeComponent } from "./modules/main/pages/home/home.page";

// Components
import { ScrollingModule } from "@angular/cdk/scrolling";
import { HTTP_INTERCEPTORS, HttpClient } from "@angular/common/http";
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MAT_MOMENT_DATE_FORMATS,
	MomentDateAdapter,
} from "@angular/material-moment-adapter";
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from "@angular/material/core";
import {
	BrowserCacheLocation,
	IPublicClientApplication,
	InteractionType,
	LogLevel,
	PublicClientApplication,
} from "@azure/msal-browser";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { environment } from "src/environments/environment";
import { AuthGuard } from "./_core/interceptors/auth-guard";
import { AuthInterceptor } from "./_core/interceptors/auth.interceptor";
import { ErrorInterceptor } from "./_core/interceptors/error.interceptor";
import { LoaderInterceptor } from "./_core/interceptors/loader.interceptor";
import { GlobalService } from "./_core/providers/global.service";
import { LocaleService } from "./_core/providers/locale.service";
import { AppRoutingModule } from "./app-routing.module";
import { CebesCardComponent } from "./modules/main/components/cards/cebe-card/cebe-card.component";
import { ContractDetailsCardComponent } from "./modules/main/components/cebe-contracts/contracts-details-card/contract-details-card.component";
import { CebePalCardComponent } from "./modules/main/components/cebe-pal/cebe-pal-card.component";
import { CebeDetailsCardComponent } from "./modules/main/components/cebe/cebe-details-card/cebe-details-card.component";
import { CebeMapCardComponent } from "./modules/main/components/cebe/cebe-map-card/cebe-map-card.component";
import { CentroDesignDataCardComponent } from "./modules/main/components/centros/centro-design-data-card/centro-design-data-card.component";
import { CentroDetailsCardComponent } from "./modules/main/components/centros/centro-details-card/centro-details-card.component";
import { CentroMapCardComponent } from "./modules/main/components/centros/centro-map-card/centro-map-card.component";
import { CentroOperationDataCardComponent } from "./modules/main/components/centros/centro-operation-data-card/centro-operation-data-card.component";
import { CentroUserReportCardComponent } from "./modules/main/components/centros/centro-user-report-card/centro-user-report-card.component";
import { ConfirmDialogComponent } from "./modules/main/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ExampleComponent } from "./modules/main/components/example/example.component";
import { SharedListaCentrosModule } from "./modules/main/components/listacentros/shared-lista-centros.module";
import { GlobeMapComponent } from "./modules/main/components/maps/globe-map/globe-map.component";
import { PersonComponent } from "./modules/main/components/person/person.component";
import { AdminPageComponent } from "./modules/main/pages/admin/admin.page";
import { CentroPageComponent } from "./modules/main/pages/centro/centro.page";
import { SortPipe } from "./modules/shared/directives/sort-pipe";
import { NoPermissionsComponent } from "./modules/shared/no-permissions/no-permissions.component";
import { MapComponent } from "./modules/main/pages/map/map.component";
import { SharedMapsModule } from "./modules/main/components/maps/shared-maps.module";

const isIE =
	window.navigator.userAgent.indexOf("MSIE ") > -1 ||
	window.navigator.userAgent.indexOf("Trident/") > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
	if (!environment.production) {
		console.log(message);
	}
}

export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: environment.azureAD.clientId,
			authority: environment.azureAD.authority,
			redirectUri: environment.azureAD.redirectUri,
			postLogoutRedirectUri: environment.azureAD.postLogoutRedirectUri,
			navigateToLoginRequestUrl: true,
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
			storeAuthStateInCookie: isIE,
		},
		system: {
			allowRedirectInIframe: true,
			loggerOptions: {
				loggerCallback: () => {},
				logLevel: LogLevel.Info,
				piiLoggingEnabled: false,
			},
		},
	});
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap: new Map([
			[
				"https://graph.microsoft.com/v1.0/me/photo/$value",
				["user.read.all"],
			],
			["https://graph.microsoft.com/v1.0/me", ["user.read"]],
			["https://graph.microsoft.com/v1.0/users", ["contacts.read"]],
			// ['https://api.powerbi.com/v1.0/myorg', ['Report.ReadWrite.All', 'Report.Read.All']]
		]),
	};
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			// scopes: ['user.read', 'contacts.read', 'Report.ReadWrite.All', 'Report.Read.All']
			scopes: ["user.read", "contacts.read"],
		},
		loginFailedRoute: "/login-failed",
	};
}

@NgModule({
	declarations: [
		AppComponent,
		NoPermissionsComponent,
		HomeComponent,
		ExamplePageComponent,
		ExampleComponent,
		CebePageComponent,
		CebesCardComponent,
		CebeDetailsCardComponent,
		ContractDetailsCardComponent,
		CebePalCardComponent,
		PersonComponent,
		CebeMapCardComponent,
		CentroPageComponent,
		CentroDetailsCardComponent,
		CentroUserReportCardComponent,
		CentroDesignDataCardComponent,
		CentroOperationDataCardComponent,
		CentroMapCardComponent,
		AdminPageComponent,
		ConfirmDialogComponent,
		GlobeMapComponent,
		SortPipe,
		MapComponent,
	],
	imports: [
		BrowserModule,
		CommonModule,
		FormsModule,
		ScrollingModule,
		BrowserAnimationsModule,
		AppRoutingModule,
		SharedModule,
		SharedMapsModule,
		SharedListaCentrosModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient],
			},
		}),
		MsalModule.forRoot(
			new PublicClientApplication({
				auth: {
					clientId: environment.azureAD.clientId,
					authority: environment.azureAD.authority,
					redirectUri: environment.azureAD.redirectUri,
					postLogoutRedirectUri:
						environment.azureAD.postLogoutRedirectUri,
					navigateToLoginRequestUrl: true,
				},
				cache: {
					cacheLocation: BrowserCacheLocation.LocalStorage,
					storeAuthStateInCookie: isIE,
				},
				system: {
					allowRedirectInIframe: true,
					loggerOptions: {
						loggerCallback: () => {},
						logLevel: LogLevel.Info,
						piiLoggingEnabled: false,
					},
				},
			}),
			{
				interactionType: InteractionType.Redirect,
			},
			{
				interactionType: InteractionType.Redirect,
				protectedResourceMap: new Map([
					[
						"https://graph.microsoft.com/v1.0/me/photo/$value",
						["user.read.all"],
					],
					["https://graph.microsoft.com/v1.0/me", ["user.read"]],
					[
						"https://graph.microsoft.com/v1.0/users",
						["contacts.read"],
					],
				]),
			},
		),
	],
	exports: [AppRoutingModule],
	providers: [
		{
			provide: LocationStrategy,
			useClass: HashLocationStrategy,
		},
		GlobalService,
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory,
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory,
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: MSALInterceptorConfigFactory,
		},
		AuthGuard,
		{
			// provide: LOCALE_ID,
			// deps: [LocaleService],
			// useValue: (localeService : LocaleService) => localeService.getLanguage()
			provide: LOCALE_ID,
			useFactory: (localeService: LocaleService) => {
				return localeService.getLanguage();
			},
			deps: [LocaleService],
		},
		{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
		{
			provide: HTTP_INTERCEPTORS,
			useClass: LoaderInterceptor,
			multi: true,
		},
		MsalService,
		MsalGuard,
		MsalBroadcastService,

		// The locale would typically be provided on the root module of your application. We do it at
		// the component level here, due to limitations of our example generation script.
		{
			provide: MAT_DATE_LOCALE,
			useFactory: (localeService: LocaleService) => {
				return localeService.getLanguage();
			},
			deps: [LocaleService],
		},

		// `MomentDateAdapter` and `MAT_MOMENT_DATE_FORMATS` can be automatically provided by importing
		// `MatMomentDateModule` in your applications root module. We provide it at the component level
		// here, due to limitations of our example generation script.
		{
			provide: DateAdapter,
			useClass: MomentDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
	],
	bootstrap: [AppComponent],
})
export class AppModule {}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
	return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}
